import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Badge,
  Button,
  Card,
  SearchIcon,
  Switch,
  TextInput,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { useAccCompanies } from '../companies/useData';
import { useAccPeriods } from '../periods/useData';
import { ReportExportButtons } from '../../../components/reports/ReportExportButtons';
import { useReportExport } from './shared/useReportExport';
import { FieldLabel, PeriodDateRangeFields } from './shared/reportFields';
import { useDefaultAccountingPeriod } from './shared/useDefaultAccountingPeriod';
import { accountingReturnState } from '../shared/navigation';

type JournalReportRow = {
  lineId: number;
  documentId: number;
  docDate: string | null;
  docNo: string | null;
  companyCode: string;
  companyName: string;
  dueDate: string | null;
  level1Name: string;
  level2Name: string;
  level3Name: string;
  level4Name: string;
  l4Code: string;
  details: string;
  nomOrd: number;
  rate: number | null;
  currencyCode: string;
  debitLocal: number;
  debitUSD: number;
  creditLocal: number;
  creditUSD: number;
  linkNo: string | null;
};

type JournalReportResponse = {
  dateFrom: string;
  dateTo: string;
  companyIds: number[];
  rows: JournalReportRow[];
  totals: {
    recordCount: number;
    debitLocal: number;
    creditLocal: number;
    debitUSD: number;
    creditUSD: number;
  };
};

const formatAmount = (value: number) =>
  value === 0 ? '' : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatRate = (value: number | null) => {
  if (value == null || value === 0) return '';
  return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

const defaultPeriodDates = () => {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().slice(0, 10) };
};

const matchesRecordSearch = (row: JournalReportRow, term: string) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.docNo,
    row.companyCode,
    row.companyName,
    row.level1Name,
    row.level2Name,
    row.level3Name,
    row.level4Name,
    row.l4Code,
    row.details,
    row.currencyCode,
    row.linkNo,
    row.docDate ? formatDateGlobal(row.docDate) : '',
    row.dueDate ? formatDateGlobal(row.dueDate) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const amountColumn = (
  key: keyof JournalReportRow,
  header: string,
  totalValue?: number,
): Column<JournalReportRow> => ({
  key,
  header,
  align: 'right',
  width: '6.5rem',
  sortable: true,
  footerType: 'custom',
  footerRender: () => (totalValue == null ? null : formatAmount(totalValue)),
  render: (value: number) => formatAmount(value),
});

export const JournalReport: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const defaults = useMemo(() => defaultPeriodDates(), []);

  const { data: companiesRaw } = useAccCompanies('', 1, 500, {
    filters: { filters: { isActive: ['true'] } },
  });
  const companies = useMemo(
    () => [...(companiesRaw ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
    [companiesRaw],
  );

  const { data: periodsRaw } = useAccPeriods('', 1, 100);
  const periods = periodsRaw ?? [];

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [allCompanies, setAllCompanies] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [recordSearch, setRecordSearch] = useState('');
  const [run, setRun] = useState(false);

  useEffect(() => {
    setSelectedCompanyIds([]);
    setAllCompanies(true);
    setCompanyFilter('');
    setPeriodId('');
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setRecordSearch('');
    setRun(false);
  }, [defaults.from, defaults.to]);

  useEffect(() => {
    if (!periodId) return;
    const period = periods.find((p) => String(p.id) === periodId);
    if (!period) return;
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
    setRun(false);
  }, [periodId, periods]);

  useDefaultAccountingPeriod(periods, (period) => {
    setPeriodId(String(period.id));
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
  });

  const filteredCompanies = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [companies, companyFilter]);

  const companyScopeLabel = allCompanies
    ? 'All companies'
    : selectedCompanyIds.length === 0
      ? 'No companies selected'
      : `${selectedCompanyIds.length} selected`;

  const toggleCompany = useCallback((id: number) => {
    setAllCompanies(false);
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
    setRun(false);
  }, []);

  const toggleAllCompanies = useCallback((checked: boolean) => {
    setAllCompanies(checked);
    setSelectedCompanyIds([]);
    setRun(false);
  }, []);

  const reportUrl = useMemo(() => {
    const canRun = run && dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0);
    if (!canRun) return null;

    const params = new URLSearchParams({
      dateFrom,
      dateTo,
    });
    if (!allCompanies && selectedCompanyIds.length > 0) {
      params.set('companyIds', selectedCompanyIds.join(','));
    }
    return `/accounting/reports/journal?${params.toString()}`;
  }, [run, allCompanies, selectedCompanyIds, dateFrom, dateTo]);

  const { data: report, isLoading } = useSWR<JournalReportResponse>(reportUrl, fetcher);
  const rows = report?.rows ?? [];

  const filteredRows = useMemo(() => {
    const q = recordSearch.trim();
    if (!q) return rows;
    return rows.filter((row) => matchesRecordSearch(row, q));
  }, [rows, recordSearch]);

  const displayTotals = useMemo(() => {
    if (!recordSearch.trim()) return report?.totals;
    let debitLocal = 0;
    let creditLocal = 0;
    let debitUSD = 0;
    let creditUSD = 0;
    for (const row of filteredRows) {
      debitLocal += row.debitLocal;
      creditLocal += row.creditLocal;
      debitUSD += row.debitUSD;
      creditUSD += row.creditUSD;
    }
    return {
      recordCount: filteredRows.length,
      debitLocal,
      creditLocal,
      debitUSD,
      creditUSD,
    };
  }, [filteredRows, recordSearch, report?.totals]);

  const periodOptions = useMemo(
    () => [
      { value: '', label: 'Custom period' },
      ...periods.map((p) => ({
        value: String(p.id),
        label: `${p.year} (${formatDateGlobal(p.startDate)} – ${formatDateGlobal(p.endDate)})`,
      })),
    ],
    [periods],
  );

  const columns = useMemo((): Column<JournalReportRow>[] => {
    const totals = displayTotals;
    const returnState = accountingReturnState(location.pathname, location.search);
    return [
      {
        key: 'docDate',
        header: 'Doc Date',
        width: '6.5rem',
        sortable: true,
        footerType: 'custom',
        footerLabel: 'Records',
        footerRender: () => String(totals?.recordCount ?? filteredRows.length),
        render: (value: string | null) => (value ? formatDateGlobal(value) : '—'),
      },
      {
        key: 'docNo',
        header: 'Doc No.',
        width: '5.5rem',
        sortable: true,
        render: (_value, row) =>
          row.docNo ? (
            <Link
              to={`/accounting/journals/${row.documentId}`}
              state={returnState}
              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {row.docNo}
            </Link>
          ) : (
            '—'
          ),
      },
      { key: 'companyCode', header: 'Cmp', width: '4rem', sortable: true },
      { key: 'companyName', header: 'Cmp. Name', width: '9rem', sortable: true },
      {
        key: 'dueDate',
        header: 'Due Date',
        width: '6.5rem',
        sortable: true,
        render: (value: string | null) => (value ? formatDateGlobal(value) : ''),
      },
      { key: 'level1Name', header: 'Level 1 Name', width: '8rem', sortable: true },
      { key: 'level2Name', header: 'Level 2 Name', width: '8rem', sortable: true },
      { key: 'level3Name', header: 'Level 3 Name', width: '8rem', sortable: true },
      { key: 'level4Name', header: 'Level 4 Name', width: '9rem', sortable: true },
      { key: 'l4Code', header: 'L4 Code', width: '7rem', sortable: true },
      { key: 'details', header: 'Details', width: '12rem', sortable: true },
      {
        key: 'nomOrd',
        header: 'Nom Ord',
        width: '4.5rem',
        align: 'center',
        sortable: true,
      },
      {
        key: 'rate',
        header: 'Rate',
        width: '4.5rem',
        align: 'right',
        sortable: true,
        render: (value: number | null) => formatRate(value),
      },
      { key: 'currencyCode', header: 'Cur.', width: '3.5rem', sortable: true },
      amountColumn('debitLocal', 'DL', totals?.debitLocal),
      amountColumn('debitUSD', 'DUSD', totals?.debitUSD),
      amountColumn('creditLocal', 'CL', totals?.creditLocal),
      amountColumn('creditUSD', 'CUSD', totals?.creditUSD),
      {
        key: 'linkNo',
        header: 'Link No.',
        width: '6rem',
        sortable: true,
        render: (value: string | null) => value ?? '',
      },
    ];
  }, [displayTotals, filteredRows.length, location.pathname, location.search]);

  const canSearch = Boolean(dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0));
  const isRecordSearchActive = recordSearch.trim().length > 0;

  const { onExportExcel, onExportPDF, exportDisabled } = useReportExport(
    filteredRows,
    columns,
    'Journal (lines)',
    'journal-lines',
    dateTo,
  );

  return (
    <div className="flex flex-col h-full min-h-0 p-4 gap-3">
      <Card className="p-4 shrink-0">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <PeriodDateRangeFields
            periodLabel="Period"
            periodId={periodId}
            periodOptions={periodOptions}
            onPeriodChange={(value) => {
              setPeriodId(value);
              setRun(false);
            }}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={(value) => {
              setPeriodId('');
              setDateFrom(value);
              setRun(false);
            }}
            onDateToChange={(value) => {
              setPeriodId('');
              setDateTo(value);
              setRun(false);
            }}
          />
          <div className="min-w-[12rem] flex-1 max-w-md">
            <FieldLabel>Search</FieldLabel>
            <TextInput
              value={recordSearch}
              onChange={(e) => setRecordSearch(e.target.value)}
              placeholder="Doc no., company, account, details…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(recordSearch)}
              disabled={!run || isLoading}
            />
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
              <span className={cn('h-1.5 w-1.5 rounded-full', allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
              {companyScopeLabel}
            </Badge>
            {run && report && (
              <ReportExportButtons
                onExportExcel={onExportExcel}
                onExportPDF={onExportPDF}
                disabled={exportDisabled || filteredRows.length === 0}
              />
            )}
            <Button
              variant="default"
              onClick={() => setRun(true)}
              disabled={!canSearch}
              className="h-10 px-5 gap-2"
            >
              <SearchIcon className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>
      </Card>

      <div
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <aside className={cn('w-56 shrink-0 border-r flex flex-col min-h-0', colors.border)}>
          <div className={cn('px-3 py-2.5 border-b', colors.border)}>
            <Switch
              label="All companies"
              checked={allCompanies}
              onChange={(e) => toggleAllCompanies(e.target.checked)}
              size="sm"
            />
          </div>
          <div className={cn('px-3 py-2 border-b', colors.border)}>
            <TextInput
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              placeholder="Filter…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(companyFilter)}
              size="sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto dashboard-scrollbar">
            {filteredCompanies.map((company) => {
              const active = !allCompanies && selectedCompanyIds.includes(company.id);
              return (
                <div
                  key={company.id}
                  className={cn(
                    'px-3 py-2 border-b transition-colors',
                    colors.border,
                    active
                      ? 'bg-indigo-50/80 dark:bg-indigo-950/30'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/40',
                  )}
                >
                  <Switch
                    label={company.name}
                    checked={allCompanies || active}
                    onChange={() => toggleCompany(company.id)}
                    size="sm"
                  />
                  <p className={cn('text-xs mt-0.5 pl-12', colors.textMuted)}>{company.code}</p>
                </div>
              );
            })}
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className={cn('flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b', colors.border, colors.surfaceHover)}>
            {run && dateFrom && dateTo && (
              <Badge variant="info" className="font-normal">
                {formatDateGlobal(dateFrom)} – {formatDateGlobal(dateTo)}
              </Badge>
            )}
            <span className={cn('text-sm', colors.textMuted)}>
              {run
                ? isLoading
                  ? 'Loading journal lines…'
                  : isRecordSearchActive
                    ? `${filteredRows.length} of ${rows.length} line${rows.length === 1 ? '' : 's'}`
                    : `${rows.length} line${rows.length === 1 ? '' : 's'}`
                : 'Select period and click Search'}
            </span>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            {!run && (
              <div className={cn('flex flex-col items-center justify-center h-full min-h-[220px] px-8 text-center', colors.textMuted)}>
                <div className={cn('h-12 w-12 rounded-full flex items-center justify-center mb-3', colors.surfaceHover)}>
                  <SearchIcon className="h-5 w-5 opacity-60" />
                </div>
                <p className="text-sm font-medium">Ready to search</p>
                <p className="text-xs mt-1 max-w-sm leading-relaxed">
                  Choose a posting period and company scope, then click Search to list all saved journal lines.
                </p>
              </div>
            )}
            {run && !isLoading && (
              <DataTable
                {...ACCOUNTING_TABLE_DEFAULTS}
                data={filteredRows}
                columns={columns}
                keyExtractor={(row) => row.lineId}
                emptyMessage={
                  isRecordSearchActive
                    ? 'No journal lines match your search.'
                    : 'No journal lines in the selected period.'
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
